import AddIcon from '@mui/icons-material/Add';
import MenuIcon from '@mui/icons-material/Menu';
import { tr } from '../lib/i18n';
import {
  Box,
  ButtonBase,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { NavLink, Outlet, useLocation, useMatch, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { localizedOrganizationName } from '../lib/i18n';
import { useOrgLogo, useOrgSocialLinks } from '../lib/org-brand';
import { AccountMenu } from './AccountMenu';
import { LanguageButton } from './LanguageButton';
import { AppBreadcrumbs } from './AppBreadcrumbs';
import { ErrorBoundary } from './ErrorBoundary';
import { NAV, type NavItem } from './nav';
import { MySubscriptionDialog, NotificationsBell, WelcomeDialog } from './notifications';
import { OrgLogo } from './org-logo';
import { PageActionsProvider, PageActionsSlot } from './page-actions';
import { ProfileDialog } from './ProfileDialog';
import { ShareButton } from './ShareLinks';
import { SupportWidget } from './SupportWidget';
import { SocialLinksBar } from './SocialLinksBar';
import { SubscriptionBanner } from './SubscriptionBanner';

export { homePath } from './nav';

const DRAWER = 256;
const RAIL = 80;
const TOPBAR = 64;
const COLLAPSE_KEY = 'org.navCollapsed';

const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
};

// ---------------------------------------------------------------------------
// navigation items
// ---------------------------------------------------------------------------
/** Live count on a nav item: offers from the public link waiting for approval. */
function useNavBadge(it: NavItem): number {
  const q = useQuery({
    queryKey: ['properties-pending-count'],
    queryFn: () => api<{ count: number }>('/properties/pending-review/count'),
    enabled: it.badge === 'pendingProperties',
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  return it.badge === 'pendingProperties' ? q.data?.count ?? 0 : 0;
}

function CountPill({ n }: { n: number }) {
  return (
    <Box component="span" sx={{ minWidth: 22, height: 20, px: 0.75, borderRadius: 999, bgcolor: 'warning.main', color: 'warning.contrastText', fontSize: 12, fontWeight: 700, display: 'inline-grid', placeItems: 'center', lineHeight: 1 }}>
      {n > 99 ? '99+' : n}
    </Box>
  );
}
function DrawerItem({ it, onNavigate }: { it: NavItem; onNavigate?: () => void }) {
  const m3 = useTheme().palette.m3;
  const active = !!useMatch({ path: it.to, end: false });
  const badge = useNavBadge(it);
  return (
    <ButtonBase
      component={NavLink}
      to={it.to}
      onClick={onNavigate}
      sx={{
        width: '100%',
        height: 40,
        px: 2,
        gap: 1.75,
        justifyContent: 'flex-start',
        borderRadius: 999,
        color: active ? m3.onSecondaryContainer : m3.onSurfaceVariant,
        bgcolor: active ? m3.secondaryContainer : 'transparent',
        fontWeight: active ? 600 : 500,
        fontSize: '0.875rem',
        transition: 'background-color 120ms',
        '&:hover': { bgcolor: active ? m3.secondaryContainer : alpha(m3.onSurface, 0.06) },
        '& svg': { fontSize: 22 },
      }}
    >
      {active ? it.activeIcon : it.icon}
      <Box component="span" sx={{ flex: 1, textAlign: 'start', whiteSpace: 'nowrap' }}>{it.label}</Box>
      {badge > 0 && (
        <Tooltip title={tr(`${badge} بانتظار الاعتماد`, `${badge} waiting for approval`)}>
          <span><CountPill n={badge} /></span>
        </Tooltip>
      )}
    </ButtonBase>
  );
}

/** Compact navigation item that keeps the expanded drawer's vertical rhythm. */
function RailItem({ it }: { it: NavItem }) {
  const m3 = useTheme().palette.m3;
  const active = !!useMatch({ path: it.to, end: false });
  const badge = useNavBadge(it);
  return (
    <Tooltip title={badge > 0 ? `${it.label} — ${tr(`${badge} بانتظار الاعتماد`, `${badge} waiting for approval`)}` : it.label} placement="right">
      <ButtonBase
        component={NavLink}
        to={it.to}
        aria-label={it.label}
        sx={{
          width: '100%',
          height: 40,
          justifyContent: 'center',
          color: active ? m3.onSecondaryContainer : m3.onSurfaceVariant,
          '& .pill': {
            width: 56,
            height: 32,
            borderRadius: 999,
            display: 'grid',
            placeItems: 'center',
            bgcolor: active ? m3.secondaryContainer : 'transparent',
            transition: 'background-color 120ms',
          },
          '&:hover .pill': { bgcolor: active ? m3.secondaryContainer : alpha(m3.onSurface, 0.08) },
          '&.Mui-focusVisible .pill': { outline: `2px solid ${m3.primary}` },
          '& svg': { fontSize: 22 },
        }}
      >
        <span className="pill" style={{ position: 'relative' }}>
          {active ? it.activeIcon : it.icon}
          {badge > 0 && <Box component="span" sx={{ position: 'absolute', top: -2, insetInlineEnd: 6 }}><CountPill n={badge} /></Box>}
        </span>
      </ButtonBase>
    </Tooltip>
  );
}

/**
 * "New entry" — the main action, reachable from every page (like Gmail's Compose):
 * extended button on top of the drawer, icon button on the rail, floating button on phones.
 */
function NewEntryButton({ variant, onNavigate }: { variant: 'drawer' | 'rail' | 'fab'; onNavigate?: () => void }) {
  const { can } = useAuth();
  const navigate = useNavigate();
  const onNew = !!useMatch('/new');
  const m3 = useTheme().palette.m3;
  if (!can('form.submit') || !can('properties.create', 'requests.create')) return null;
  if (variant === 'fab' && onNew) return null;
  const go = () => {
    onNavigate?.();
    navigate('/new');
  };
  const label = tr('إدخال جديد', 'New entry');
  const base = {
    bgcolor: m3.primaryContainer,
    color: m3.onPrimaryContainer,
    boxShadow: '0 1px 3px rgba(0,0,0,.15), 0 4px 8px 3px rgba(0,0,0,.08)',
    transition: 'box-shadow .2s, background-color .2s',
    '&:hover': { bgcolor: alpha(m3.primary, 0.22), boxShadow: '0 2px 3px rgba(0,0,0,.15), 0 6px 10px 4px rgba(0,0,0,.1)' },
    '&.Mui-focusVisible': { outline: `2px solid ${m3.primary}`, outlineOffset: 2 },
    '& svg': { fontSize: 24 },
  } as const;
  if (variant === 'drawer') {
    return (
      <ButtonBase onClick={go} aria-current={onNew ? 'page' : undefined} sx={{ ...base, alignSelf: 'flex-start', height: 56, px: 2.5, gap: 1.5, borderRadius: '16px', fontSize: 15, fontWeight: 600, mb: 1.5, mt: 0.5 }}>
        <AddIcon />
        {label}
      </ButtonBase>
    );
  }
  if (variant === 'rail') {
    return (
      <Tooltip title={label} placement={theme_dir() === 'rtl' ? 'left' : 'right'}>
        <ButtonBase onClick={go} aria-label={label} sx={{ ...base, width: 56, height: 56, borderRadius: '16px', mb: 1.5, mt: 0.5, flexShrink: 0 }}>
          <AddIcon />
        </ButtonBase>
      </Tooltip>
    );
  }
  return (
    <ButtonBase
      onClick={go}
      aria-label={label}
      sx={{ ...base, position: 'fixed', insetInlineEnd: 16, bottom: 'calc(16px + env(safe-area-inset-bottom))', zIndex: 1100, height: 56, px: 2, gap: 1, borderRadius: '16px', fontSize: 15, fontWeight: 600 }}
    >
      <AddIcon />
      {label}
    </ButtonBase>
  );
}
const theme_dir = () => (typeof document !== 'undefined' && document.documentElement.dir === 'ltr' ? 'ltr' : 'rtl');

function useVisibleNav() {
  const { can } = useAuth();
  return NAV.map((g) => ({ ...g, items: g.items.filter((it) => !it.perms || can(...it.perms)) })).filter((g) => g.items.length);
}

function DrawerContent({ onNavigate }: { onNavigate?: () => void }) {
  const groups = useVisibleNav();
  const socialLinks = useOrgSocialLinks();
  const m3 = useTheme().palette.m3;
  return (
    <Stack sx={{ height: '100%', width: DRAWER, px: 1.5, pt: 1, pb: 1 }}>
      <NewEntryButton variant="drawer" onNavigate={onNavigate} />
      <Box component="nav" aria-label={tr('القائمة الرئيسية', 'Main navigation')} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', mx: -0.5, px: 0.5, display: 'flex', flexDirection: 'column' }}>
        {groups.map((g, gi) => (
          <Box key={gi} sx={{ mb: 1, mt: gi > 0 ? 'auto' : 0, pt: gi > 0 ? 2 : 0 }}>
            {g.label && (
              <Typography variant="caption" sx={{ display: 'block', px: 2, pt: 1.5, pb: 0.75, color: m3.onSurfaceVariant, fontWeight: 500 }}>
                {g.label}
              </Typography>
            )}
            <Stack spacing={0.25}>
              {g.items.map((it) => <DrawerItem key={it.to} it={it} onNavigate={onNavigate} />)}
            </Stack>
          </Box>
        ))}
      </Box>
      {socialLinks.length > 0 && (
        <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 0.5, color: m3.onSurfaceVariant }}>
          <SocialLinksBar links={socialLinks} />
        </Box>
      )}
    </Stack>
  );
}

function RailContent() {
  const groups = useVisibleNav();
  const socialLinks = useOrgSocialLinks();
  const m3 = useTheme().palette.m3;
  return (
    <Stack alignItems="center" sx={{ height: '100%', width: RAIL, px: 0.75, pt: 1, pb: 1 }}>
      <NewEntryButton variant="rail" />
      <Box component="nav" aria-label={tr('القائمة الرئيسية', 'Main navigation')} sx={{ width: '100%', flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        {groups.map((g, gi) => (
          <Box key={gi} sx={{ mb: 1, mt: gi > 0 ? 'auto' : 0, pt: gi > 0 ? 2 : 0 }}>
            <Stack spacing={0.25}>
              {g.items.map((it) => <RailItem key={it.to} it={it} />)}
            </Stack>
          </Box>
        ))}
      </Box>
      {socialLinks.length > 0 && (
        <Box sx={{ width: '100%', borderTop: 1, borderColor: 'divider', pt: 0.5, color: m3.onSurfaceVariant }}>
          <SocialLinksBar links={socialLinks} />
        </Box>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// shell
// ---------------------------------------------------------------------------
export function Layout() {
  const { me } = useAuth();
  const theme = useTheme();
  const m3 = theme.palette.m3;
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const logoUrl = useOrgLogo();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [subscriptionOpen, setSubscriptionOpen] = useState(false);
  const { pathname } = useLocation();
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scroller.current?.scrollTo(0, 0);
    setMobileOpen(false);
  }, [pathname]);

  const toggleNav = () => {
    if (!desktop) return setMobileOpen((o) => !o);
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, c ? '0' : '1');
      } catch {
        /* ignore */
      }
      return !c;
    });
  };

  const sideWidth = !desktop ? 0 : collapsed ? RAIL : DRAWER;
  const organizationName = localizedOrganizationName(me?.organization);

  return (
    <PageActionsProvider>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: m3.surface, overflow: 'hidden' }}>
        {/* ---------------- top app bar ---------------- */}
        <Box
          component="header"
          sx={{
            height: TOPBAR,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.5, md: 1 },
            px: { xs: 1, md: 1.5 },
            bgcolor: m3.surfaceContainerLowest,
            borderBottom: 1,
            borderColor: m3.outlineVariant,
            position: 'relative',
            zIndex: 1201,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={0}
            sx={{
              width: { md: collapsed ? sideWidth : Math.max(0, sideWidth - 12) },
              marginInlineStart: { md: collapsed ? -1.5 : 0 },
              flexShrink: 0,
              minWidth: 0,
              transition: 'width 200ms cubic-bezier(.2,0,0,1), margin 200ms cubic-bezier(.2,0,0,1)',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0, display: { xs: 'none', sm: collapsed ? 'none' : 'flex' } }}>
              <OrgLogo src={logoUrl} variant="rounded" sx={{ width: 36, height: 36, fontSize: 17, borderRadius: '10px' }}>
                {organizationName[0]}
              </OrgLogo>
              <Typography noWrap sx={{ fontSize: '1.25rem', fontWeight: 400, color: m3.onSurfaceVariant, minWidth: 0 }}>
                {organizationName}
              </Typography>
            </Stack>
            <Tooltip title={tr('القائمة الرئيسية', 'Main navigation')}>
              <IconButton
                onClick={toggleNav}
                aria-label={tr('القائمة الرئيسية', 'Main navigation')}
                sx={{ color: m3.onSurfaceVariant, p: 1.5, marginInlineStart: 'auto', marginInlineEnd: collapsed ? 'auto' : 0 }}
              >
                <MenuIcon />
              </IconButton>
            </Tooltip>
          </Stack>

          <Box sx={{ flex: 1 }} />

          <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flexShrink: 0, color: m3.onSurfaceVariant, '& .MuiIconButton-root': { color: 'inherit' } }}>
            <LanguageButton compact />
            <ShareButton />
            <NotificationsBell />
            <AccountMenu
              onProfile={() => setProfileOpen(true)}
              onSettings={() => navigate('/settings')}
              onSubscription={() => setSubscriptionOpen(true)}
            />
          </Stack>
        </Box>

        {/* ---------------- body ---------------- */}
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
          {desktop ? (
            <Box sx={{ width: sideWidth, flexShrink: 0, overflow: 'hidden', bgcolor: m3.surfaceContainerLow, borderInlineEnd: 1, borderColor: m3.outlineVariant, transition: 'width 200ms cubic-bezier(.2,0,0,1)' }}>
              {collapsed ? <RailContent /> : <DrawerContent />}
            </Box>
          ) : (
            <Drawer
              open={mobileOpen}
              onClose={() => setMobileOpen(false)}
              ModalProps={{ keepMounted: true }}
              PaperProps={{ sx: { borderStartEndRadius: 16, borderEndEndRadius: 16, pt: 1, bgcolor: m3.surfaceContainerLow } }}
            >
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 3, py: 1.5 }}>
                <OrgLogo src={logoUrl} variant="rounded" sx={{ width: 32, height: 32, fontSize: 15, borderRadius: '9px' }}>
                  {organizationName[0]}
                </OrgLogo>
                <Typography noWrap sx={{ fontSize: '1.125rem', color: m3.onSurfaceVariant }}>{organizationName}</Typography>
              </Stack>
              <DrawerContent onNavigate={() => setMobileOpen(false)} />
            </Drawer>
          )}

          <Box
            component="main"
            ref={scroller}
            sx={{
              flex: 1,
              minWidth: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              px: { xs: 1.5, md: 3, xl: 4 },
              // phones: room for the floating "New entry" button
              // room for the floating buttons (support, mobile «new entry») so they never cover the page's last row / pager
              pb: { xs: 11, md: 10 },
            }}
          >
            {/* page header: title (or breadcrumbs) + page actions */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{
                minHeight: 64,
                py: 1.25,
                flexWrap: 'wrap',
                rowGap: 1,
                position: 'sticky',
                top: 0,
                zIndex: 10,
                bgcolor: m3.surface,
                borderBottom: 1,
                borderColor: m3.outlineVariant,
                mb: 2,
              }}
            >
              <AppBreadcrumbs />
              <PageActionsSlot />
            </Stack>
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <SubscriptionBanner />
              <ErrorBoundary resetKey={pathname}>
                <Outlet />
              </ErrorBoundary>
            </Box>
            {!desktop && <NewEntryButton variant="fab" />}
          </Box>
        </Box>
      </Box>

      <SupportWidget />
      <ProfileDialog open={profileOpen} onClose={() => setProfileOpen(false)} />
      <MySubscriptionDialog open={subscriptionOpen} onClose={() => setSubscriptionOpen(false)} />
      <WelcomeDialog />
    </PageActionsProvider>
  );
}
